import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Download, RefreshCw, AlertTriangle, CheckCircle, Search, Receipt, FileText, ScanText } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { purchasesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import PurchaseEditor from './PurchaseEditor';

const eur = (v) => (v == null ? '—'
  : `${Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
const pct = (v) => (v == null ? '—'
  : `${v > 0 ? '+' : ''}${(v * 100).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`);
const shortDate = (d) => {
  try { return d ? format(parseISO(d), 'dd/MM/yy', { locale: fr }) : '—'; } catch { return d || '—'; }
};
const norm = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const CATEGORY_LABEL = {
  chantier: 'Achat de chantier', 'main-oeuvre': "Main-d'œuvre", 'hors-chantier': 'Hors chantier', 'a-classer': 'À classer',
};
const FILTERS = [
  { key: 'a-traiter', label: 'À traiter', test: (p) => p.category === 'chantier' && p.status !== 'affectee' },
  { key: 'affectees', label: 'Affectées', test: (p) => p.category === 'chantier' && p.status === 'affectee' },
  { key: 'a-classer', label: 'À classer', test: (p) => p.category === 'a-classer' },
  { key: 'autres', label: "Main-d'œuvre et hors chantier", test: (p) => p.category === 'main-oeuvre' || p.category === 'hors-chantier' },
];
// Au-delà de ±8 % d'écart entre réel et estimé, l'estimation du devis est à revoir.
const GAP_THRESHOLD = 0.08;
const gapClass = (v) => {
  if (v == null) return 'text-gray-400';
  if (v > GAP_THRESHOLD) return 'text-red-600';
  if (v < -GAP_THRESHOLD) return 'text-blue-600';
  return 'text-green-700';
};

const ProgressBadge = ({ purchase }) => {
  if (purchase.category !== 'chantier') return <Badge variant="outline">{CATEGORY_LABEL[purchase.category]}</Badge>;
  if (purchase.status === 'a-detailler') {
    const lus = (purchase.suggestedLines || []).length;
    return lus
      ? <Badge className="bg-blue-100 text-blue-800" title="BL lus automatiquement dans le PDF : à vérifier puis enregistrer">À valider · {lus} BL lus</Badge>
      : <Badge className="bg-orange-100 text-orange-800">À détailler</Badge>;
  }
  if (purchase.status === 'a-affecter') {
    return (
      <Badge className="bg-yellow-100 text-yellow-800">
        {purchase.lines.length} BL · reste {eur(purchase.unassignedHT + purchase.remainderHT)}
      </Badge>
    );
  }
  return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" /> Affectée</Badge>;
};

const PurchasesSummary = ({ summary, onOpenBl }) => {
  const [search, setSearch] = useState('');
  // Du plus récent au plus ancien par défaut ; un clic sur « Fin » inverse le sens.
  const [endDesc, setEndDesc] = useState(true);
  if (!summary) return null;
  const { totals, chantiers, suppliers } = summary;
  const cards = [
    ['Achats de chantier', totals.invoicesHT, 'text-gray-800'],
    ['Affecté aux chantiers', totals.assignedHT, 'text-green-700'],
    ['Consommables', totals.consumableHT, 'text-gray-700'],
    ['Stock', totals.stockHT || 0, 'text-blue-700'],
    ['Frais de facturation', totals.feesHT || 0, 'text-gray-700'],
    ['Anciens chantiers', totals.priorHT || 0, 'text-gray-500'],
    ['Non affecté', totals.unassignedHT, 'text-orange-700'],
  ];
  const q = norm(search).trim();
  // Les chantiers sans date de fin passent en dernier, quel que soit le sens du tri.
  const byEnd = (a, b) => {
    if (!a.endDate && !b.endDate) return (a.name || '').localeCompare(b.name || '', 'fr');
    if (!a.endDate) return 1;
    if (!b.endDate) return -1;
    return (endDesc ? -1 : 1) * a.endDate.localeCompare(b.endDate) || (a.name || '').localeCompare(b.name || '', 'fr');
  };
  const shown = chantiers.filter((c) => !q || norm(c.name).includes(q)).sort(byEnd);
  const withReal = shown.filter((c) => c.realHT > 0);
  const withoutReal = shown.filter((c) => c.realHT <= 0);
  // Recherche en cours : le total suit les lignes affichées, pas l'ensemble des chantiers.
  const filtered = Boolean(q);
  const sumReal = withReal.reduce((s, c) => s + (c.realHT || 0), 0);
  const sumEstimate = withReal.reduce((s, c) => s + (c.estimateHT || 0), 0);
  const totalReal = filtered ? sumReal : totals.chantiersRealHT;
  const totalEstimate = filtered ? sumEstimate : totals.chantiersEstimateHT;
  const totalDiffPct = filtered ? (sumEstimate ? (sumReal - sumEstimate) / sumEstimate : null) : totals.chantiersDiffPct;
  const chantierRow = (c) => (
    <tr key={c.projectId || c.legacyChantierId || c.name} className="border-t border-gray-100">
      <td className="px-3 py-1.5 text-gray-900">
        <button type="button" onClick={() => onOpenBl(c)} className="text-left hover:underline"
                title="Voir les BL de ce chantier">
          {c.name}
        </button>
        {c.legacyChantierId && <span className="ml-1 text-xs text-gray-400">(classeur)</span>}
      </td>
      <td className="px-3 py-1.5 whitespace-nowrap text-gray-600" title="Fin réelle, ou prévue si le chantier n'est pas terminé">
        {shortDate(c.endDate)}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums">{eur(c.realHT)}</td>
      <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">{eur(c.estimateHT)}</td>
      <td className="px-3 py-1.5 text-right tabular-nums">{c.diffHT == null ? '—' : eur(c.diffHT)}</td>
      <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${gapClass(c.diffPct)}`}>{pct(c.diffPct)}</td>
      <td className="px-3 py-1.5 text-center" title={c.consolidated ? 'Achats réputés complets' : 'Achats encore partiels'}>
        {c.consolidated ? '✅' : '⏳'}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums">
        {c.blCount > 0 ? (
          <button type="button" onClick={() => onOpenBl(c)}
                  className="inline-flex items-center gap-1 text-blue-700 hover:underline"
                  title="Voir, vérifier et modifier les BL de ce chantier">
            <FileText className="w-3.5 h-3.5" />{c.blCount}
          </button>
        ) : <span className="text-gray-400">0</span>}
      </td>
    </tr>
  );

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        {cards.map(([label, value, cls]) => (
          <div key={label} className="bg-white border border-gray-200 rounded-lg p-3">
            <div className={`text-xl font-semibold tabular-nums ${cls}`}>{eur(value)}</div>
            <div className="text-xs text-gray-500">{label}</div>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-800">Par chantier : réel vs estimé</h3>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Rechercher un chantier" className="pl-8 h-8 w-64" />
          </div>
        </div>
        <p className="text-xs text-gray-500">
          Estimé = « Achats HT » saisi au devis. Écart en rouge : sous-estimé (plus de 8 %) ; en bleu :
          sur-estimé (moins 8 %). ✅ = achats réputés complets (un mois après la fin du chantier) ; ⏳ = encore partiels.
          Classés par date de fin (clic sur « Fin » pour inverser).
        </p>
        <div className="border border-gray-200 rounded-lg overflow-x-auto bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-600">
                <th className="px-3 py-2 font-medium">Chantier</th>
                <th className="px-3 py-2 font-medium">
                  <button type="button" onClick={() => setEndDesc((d) => !d)} className="hover:underline"
                          title="Fin réelle, ou prévue si le chantier n'est pas terminé — cliquer pour inverser le tri">
                    Fin {endDesc ? '↓' : '↑'}
                  </button>
                </th>
                <th className="px-3 py-2 font-medium text-right">Réel HT</th>
                <th className="px-3 py-2 font-medium text-right">Estimé HT</th>
                <th className="px-3 py-2 font-medium text-right">Écart</th>
                <th className="px-3 py-2 font-medium text-right">Écart %</th>
                <th className="px-3 py-2 font-medium text-center">Consolidé</th>
                <th className="px-3 py-2 font-medium text-right">BL</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-500">Aucun chantier ne correspond à « {search} ».</td></tr>
              )}
              {withReal.map(chantierRow)}
              {withReal.length > 0 && (
                <tr className="border-t-2 border-gray-200 bg-gray-50 font-medium">
                  <td className="px-3 py-1.5" colSpan={2}>
                    {filtered ? 'Total des chantiers affichés ayant des achats' : 'Total des chantiers ayant des achats'}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{eur(totalReal)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{eur(totalEstimate)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{eur(totalReal - totalEstimate)}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums ${gapClass(totalDiffPct)}`}>{pct(totalDiffPct)}</td>
                  <td colSpan={2} />
                </tr>
              )}
              {withoutReal.map(chantierRow)}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-gray-800">Par fournisseur</h3>
        <div className="border border-gray-200 rounded-lg overflow-x-auto bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-600">
                <th className="px-3 py-2 font-medium">Fournisseur</th>
                <th className="px-3 py-2 font-medium text-right">Total HT</th>
                <th className="px-3 py-2 font-medium text-right">Factures</th>
                <th className="px-3 py-2 font-medium text-right">BL</th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map((s) => (
                <tr key={s.supplierName} className="border-t border-gray-100">
                  <td className="px-3 py-1.5 text-gray-900">{s.supplierName}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{eur(s.amountHT)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">{s.invoices}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">{s.lines}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Les BL d'un chantier, tous fournisseurs confondus : de quoi vérifier d'où vient le
// « réel » du tableau de bord. Un clic ouvre la facture d'origine (PDF + lignes),
// où le BL se corrige ou se réaffecte ; il quitte alors cette liste s'il change de chantier.
const ChantierBlDialog = ({ chantier, purchases, onOpenPurchase, onClose }) => {
  const rows = useMemo(() => {
    const out = [];
    purchases.filter((p) => p.category === 'chantier').forEach((p) => {
      (p.lines || []).forEach((l) => {
        if (l.target !== 'project') return;
        const same = chantier.projectId ? l.projectId === chantier.projectId
          : l.legacyChantierId === chantier.legacyChantierId;
        if (same) out.push({ purchase: p, line: l });
      });
    });
    return out.sort((a, b) => (b.line.blDate || b.purchase.invoiceDate || '')
      .localeCompare(a.line.blDate || a.purchase.invoiceDate || ''));
  }, [purchases, chantier]);
  const total = rows.reduce((sum, r) => sum + Number(r.line.amountHT || 0), 0);

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white border border-gray-200">
        <DialogHeader>
          <DialogTitle className="flex items-center text-gray-800">
            <FileText className="w-5 h-5 mr-2" /> BL — {chantier.name}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-gray-500">
          Cliquez une ligne pour ouvrir la facture d'origine : le PDF s'affiche à côté, et le BL se corrige
          ou se réaffecte à un autre chantier.
        </p>
        {rows.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">Aucun BL affecté à ce chantier.</p>
        ) : (
          <div className="border border-gray-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs text-gray-600">
                  <th className="px-3 py-2 font-medium">Date BL</th>
                  <th className="px-3 py-2 font-medium">N° BL</th>
                  <th className="px-3 py-2 font-medium">Réf. commande</th>
                  <th className="px-3 py-2 font-medium">Fournisseur</th>
                  <th className="px-3 py-2 font-medium">Facture</th>
                  <th className="px-3 py-2 font-medium text-right">Montant HT</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ purchase, line }, i) => (
                  <tr key={line.id || `${purchase.id}-${i}`} onClick={() => onOpenPurchase(purchase)}
                      className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer">
                    <td className="px-3 py-1.5 whitespace-nowrap text-gray-600">{shortDate(line.blDate)}</td>
                    <td className="px-3 py-1.5 text-gray-900">{line.blNumber || '—'}</td>
                    <td className="px-3 py-1.5 text-gray-700">{line.reference || '—'}</td>
                    <td className="px-3 py-1.5 text-gray-700">{purchase.supplierName}</td>
                    <td className="px-3 py-1.5 text-gray-600">
                      {purchase.invoiceNumber} <span className="text-xs text-gray-400">({shortDate(purchase.invoiceDate)})</span>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{eur(line.amountHT)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-gray-200 bg-gray-50 font-medium">
                  <td className="px-3 py-1.5" colSpan={5}>Total ({rows.length} BL)</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{eur(total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PurchasesView = () => {
  const { toast } = useToast();
  const [purchases, setPurchases] = useState([]);
  const [context, setContext] = useState({ projects: [], legacyChantiers: [], suppliers: [], referenceSuggestions: {} });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [filter, setFilter] = useState('a-traiter');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [blChantier, setBlChantier] = useState(null);

  const load = useCallback(async () => {
    try {
      const [list, ctx, sum] = await Promise.all([purchasesAPI.list(), purchasesAPI.context(), purchasesAPI.summary()]);
      setPurchases(list);
      setContext(ctx);
      setSummary(sum);
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de charger les achats', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  // Lecture automatique des PDF (pré-remplissage des BL) : tâche de fond côté serveur,
  // suivie ici toutes les 4 s tant qu'elle tourne (elle peut aussi avoir démarré seule, après un import).
  const [autoRead, setAutoRead] = useState({ running: false, done: 0, total: 0, errors: 0 });
  useEffect(() => { purchasesAPI.autoReadStatus().then(setAutoRead).catch(() => {}); }, []);
  useEffect(() => {
    if (!autoRead.running) return undefined;
    const timer = setTimeout(async () => {
      try {
        const s = await purchasesAPI.autoReadStatus();
        setAutoRead(s);
        if (!s.running) {
          await load();
          toast({ title: 'Lecture des PDF terminée',
                  description: `${s.done} facture(s) lue(s)${s.errors ? `, ${s.errors} en erreur` : ''} : les propositions sont marquées « À valider ».`, duration: 6000 });
        }
      } catch (error) {
        setAutoRead((a) => ({ ...a, running: false }));
      }
    }, 4000);
    return () => clearTimeout(timer);
  }, [autoRead, load, toast]);

  const handleAutoRead = async () => {
    try {
      const r = await purchasesAPI.autoReadAll();
      setAutoRead(r);
      toast({ title: r.started ? 'Lecture des PDF lancée' : 'Lecture déjà en cours',
              description: 'Elle tourne en tâche de fond ; les factures lues passent en « À valider ».', duration: 6000 });
    } catch (error) {
      toast({ title: 'Lecture impossible', description: error?.response?.data?.detail || String(error), variant: 'destructive' });
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const r = await purchasesAPI.importFromPennylane();
      if (!r.configured) {
        toast({ title: 'Pennylane non configuré', description: r.reason, variant: 'destructive', duration: 6000 });
        return;
      }
      toast({
        title: 'Import Pennylane terminé',
        description: `${r.created} nouvelle(s) facture(s), ${r.updated} mise(s) à jour, ${r.filesDownloaded} PDF récupéré(s)`
          + (r.duplicates ? ` · ${r.duplicates} facture(s) en double dans Pennylane, regroupée(s)` : ''),
        duration: 8000,
      });
      await load();
    } catch (error) {
      toast({ title: "Échec de l'import", description: error?.response?.data?.detail || String(error), variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  };

  const handleSupplierCategory = async (supplier, category) => {
    try {
      const r = await purchasesAPI.setSupplierCategory(supplier.key, category);
      toast({ title: `${supplier.name} : ${CATEGORY_LABEL[category]}`, description: `${r.invoicesUpdated} facture(s) reclassée(s)`, duration: 3000 });
      await load();
    } catch (error) {
      toast({ title: 'Classement impossible', description: error?.response?.data?.detail || String(error), variant: 'destructive' });
    }
  };

  const handleSaved = (updated) => {
    setPurchases((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    purchasesAPI.summary().then(setSummary).catch(() => {});
    purchasesAPI.context().then(setContext).catch(() => {});
  };

  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f.key, purchases.filter(f.test).length])), [purchases]);
  const visible = useMemo(() => {
    const current = FILTERS.find((f) => f.key === filter);
    const q = norm(search).trim();
    return purchases.filter(current.test).filter((p) => !q || norm(
      `${p.supplierName} ${p.invoiceNumber} ${(p.lines || []).map((l) => `${l.blNumber} ${l.reference}`).join(' ')}`,
    ).includes(q));
  }, [purchases, filter, search]);
  const suppliersToClassify = context.suppliers.filter((s) => s.category === 'a-classer');

  if (loading) {
    return (
      <p className="text-sm text-gray-500 flex items-center">
        <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Chargement des achats…
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center">
          <Receipt className="w-5 h-5 mr-2" /> Achats
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={handleAutoRead} disabled={autoRead.running} variant="outline" size="sm"
                  className="border-blue-200 text-blue-700 hover:bg-blue-50"
                  title="Lit les BL dans le PDF des factures à détailler et pré-remplit les lignes ; vous validez ensuite">
            <ScanText className={`w-4 h-4 mr-1.5 ${autoRead.running ? 'animate-pulse' : ''}`} />
            {autoRead.running ? `Lecture des PDF… ${autoRead.done}/${autoRead.total}` : 'Lire les PDF à détailler'}
          </Button>
          <Button onClick={handleImport} disabled={importing} variant="outline" size="sm"
                  className="border-green-200 text-green-700 hover:bg-green-50"
                  title="Récupérer les nouvelles factures fournisseurs de Pennylane">
            <Download className={`w-4 h-4 mr-1.5 ${importing ? 'animate-pulse' : ''}`} />
            {importing ? 'Import en cours…' : 'Importer depuis Pennylane'}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="factures">
        <TabsList>
          <TabsTrigger value="factures">Factures</TabsTrigger>
          <TabsTrigger value="bilan">Tableau de bord</TabsTrigger>
        </TabsList>

        <TabsContent value="factures" className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {FILTERS.map((f) => (
              <button key={f.key} type="button" onClick={() => setFilter(f.key)}
                      className={`px-3 py-1 rounded-full text-sm border ${filter === f.key
                        ? 'bg-gray-800 text-white border-gray-800'
                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}>
                {f.label} <span className="opacity-70">{counts[f.key]}</span>
              </button>
            ))}
            <div className="relative ml-auto">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                     placeholder="Fournisseur, n° facture, BL, réf." className="pl-8 h-8 w-64" />
            </div>
          </div>

          {filter === 'a-classer' && suppliersToClassify.length > 0 && (
            <div className="border border-orange-200 bg-orange-50 rounded-lg p-3 space-y-2">
              <p className="text-sm text-orange-800">
                Classez un fournisseur une fois : ses factures à classer suivent, et les prochaines aussi.
              </p>
              {suppliersToClassify.map((s) => (
                <div key={s.key} className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium text-gray-800 min-w-[14rem]">{s.name}</span>
                  {['chantier', 'main-oeuvre', 'hors-chantier'].map((c) => (
                    <Button key={c} size="sm" variant="outline" className="h-7" onClick={() => handleSupplierCategory(s, c)}>
                      {CATEGORY_LABEL[c]}
                    </Button>
                  ))}
                </div>
              ))}
            </div>
          )}

          {visible.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">Aucune facture dans cette liste.</p>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-x-auto bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs text-gray-600">
                    <th className="px-3 py-2 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium">Fournisseur</th>
                    <th className="px-3 py-2 font-medium">N° facture</th>
                    <th className="px-3 py-2 font-medium text-right">Montant HT</th>
                    <th className="px-3 py-2 font-medium">Avancement</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((p) => (
                    <tr key={p.id} onClick={() => setEditing(p)} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer">
                      <td className="px-3 py-2 whitespace-nowrap text-gray-600">{shortDate(p.invoiceDate)}</td>
                      <td className="px-3 py-2 text-gray-900">
                        {p.supplierName}
                        {p.source !== 'pennylane' && (
                          <span className="ml-1 text-xs text-gray-400">({p.source === 'classeur' ? 'classeur' : 'saisie manuelle'})</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {p.invoiceNumber}
                        {p.amountWarning && !p.amountWarningAcknowledged && (
                          <span title={p.amountWarning} className="inline-flex ml-1 align-middle text-orange-500">
                            <AlertTriangle className="w-3.5 h-3.5" />
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{eur(p.amountHT)}</td>
                      <td className="px-3 py-2"><ProgressBadge purchase={p} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="bilan">
          <PurchasesSummary summary={summary} onOpenBl={setBlChantier} />
        </TabsContent>
      </Tabs>

      {blChantier && !editing && (
        <ChantierBlDialog chantier={blChantier} purchases={purchases}
                          onOpenPurchase={setEditing} onClose={() => setBlChantier(null)} />
      )}

      {editing && (
        <PurchaseEditor purchase={editing} context={context} isOpen={Boolean(editing)}
                        onOpenChange={(open) => { if (!open) setEditing(null); }} onSaved={handleSaved} />
      )}
    </div>
  );
};

export default PurchasesView;
